import React, {useEffect, useMemo, useState} from 'react';
import {useSelector} from 'react-redux';
import {IValidationErrorObject} from '../../../../incase-domain/common/validationErrorResponse';
import {ELEMENT} from '../../../../incase-domain/elements/base/ElementTypes';
import {FLIP_CARD_SIDE, IElementFlipCard, IFlipCardElements} from '../../../../incase-domain/elements/flipCard';
import {IElementHtml} from '../../../../incase-domain/elements/html/html';
import {IElementImage} from '../../../../incase-domain/elements/image';
import {IElementStaticList} from '../../../../incase-domain/elements/staticList';
import {IElementStaticText} from '../../../../incase-domain/elements/staticText';
import {selectedSlideSelector} from '../../../../store/selectors/slide.selectors';
import {updateFlipCardImage} from '../../../../utils/flipCard';
import {useSlideUpdateContext} from '../../../../ui/context/SetSlideUpdateContext';
import {ELEMENT_TABS} from '../../form/part/tabs';
import {handleHighlightedElementChange} from '../../form/slide.form.utils';
import HighlightedHtmlElementComponent from '../html/html';
import HighlightedImageElementComponent from '../media/image';
import HighlightedStaticListElementComponent from '../staticList';
import HighlightedStaticTextElementComponent from '../text/staticText';

interface FlipCardElementProps {
    selectedType: ELEMENT | undefined;
    flipCardSide: FLIP_CARD_SIDE;
    errors: IValidationErrorObject | null;
    highlightedElement: IElementFlipCard;
    highlightedElementChangeHandler: {single: (field: string, value: unknown) => void};
}

export default function FlipCardElement(props: FlipCardElementProps): JSX.Element {
    const {selectedType, errors, flipCardSide, highlightedElement, highlightedElementChangeHandler} = props;

    const slide = useSelector(selectedSlideSelector);

    const [activeTab, setActiveTab] = useState('');

    let highlighted;

    const elIndex: number = slide.elements.findIndex((el) => el.id === highlightedElement.id);

    const {setSlide} = useSlideUpdateContext();

    useEffect(() => {
        if (!selectedType) return;
        setActiveTab(ELEMENT_TABS[selectedType] && ELEMENT_TABS[selectedType][0].id);
    }, [selectedType]);

    const elementErrors: IValidationErrorObject | null = useMemo(() => {
        if (!errors) return null;

        const hasElementErrors =
            errors.flipCardElement &&
            Array.isArray(errors.flipCardElement) &&
            errors.flipCardElement[0] &&
            errors.flipCardElement[0][flipCardSide];

        if (hasElementErrors && Array.isArray(errors.flipCardElement)) {
            return errors.flipCardElement[0][flipCardSide][0] as IValidationErrorObject;
        }

        return null;
    }, [errors, flipCardSide]);

    const handleElementChange = (field: keyof IFlipCardElements | string, value: unknown): void => {
        const updatedElement = {
            ...highlightedElement.flipCardElement[flipCardSide],
            [field]: value,
        };

        highlightedElementChangeHandler.single('flipCardElement', {
            ...highlightedElement.flipCardElement,
            [flipCardSide]: updatedElement,
        });
    };

    const updateImageHandler = (fields: string[], values: unknown[]): void => {
        const newSlide = {...slide};

        newSlide.elements[elIndex] = updateFlipCardImage(fields, values, newSlide.elements[elIndex] as IElementFlipCard, flipCardSide);

        handleHighlightedElementChange(newSlide, elIndex, setSlide, newSlide.elements[elIndex]);
    };

    switch (selectedType) {
        case ELEMENT.IMAGE:
            highlighted = (
                <HighlightedImageElementComponent
                    activeTab={activeTab}
                    overrideHighlightedElement={highlightedElement.flipCardElement[flipCardSide] as IElementImage}
                    errors={elementErrors}
                    overrideHighlightedElementChangeHandler={{single: handleElementChange}}
                    updateImageHandler={updateImageHandler}
                />
            );
            break;
        case ELEMENT.STATIC_TEXT:
            highlighted = (
                <HighlightedStaticTextElementComponent
                    activeTab={activeTab}
                    overrideHighlightedElement={highlightedElement.flipCardElement[flipCardSide] as IElementStaticText}
                    overrideHighlightedElementChangeHandler={{single: handleElementChange}}
                    isFlipCard
                />
            );
            break;
        case ELEMENT.HTML:
            highlighted = (
                <HighlightedHtmlElementComponent
                    activeTab={activeTab}
                    overrideHighlightedElement={highlightedElement.flipCardElement[flipCardSide] as IElementHtml}
                    overrideHighlightedElementChangeHandler={{single: handleElementChange}}
                    isFlipCard
                />
            );
            break;
        case ELEMENT.STATIC_LIST:
            highlighted = (
                <HighlightedStaticListElementComponent
                    activeTab={activeTab}
                    overrideHighlightedElement={highlightedElement.flipCardElement[flipCardSide] as IElementStaticList}
                    errors={elementErrors}
                    overrideHighlightedElementChangeHandler={{single: handleElementChange}}
                />
            );
            break;
        default:
            return <div />;
    }

    return (
        <>
            <hr />
            {highlighted}
        </>
    );
}
